import React                from "react";
import Box                  from "@mui/material/Box";
import Link                 from "@mui/material/Link";
import Button               from "@mui/material/Button";
import Typography           from "@mui/material/Typography";
import TextField            from "@mui/material/TextField";
import FileUploadOutlined   from '@mui/icons-material/FileUploadOutlined';
import {
    ADD_STUDENTS_PLACEHOLDER,
    ADD_STUDENTS_BUTTON,
    ADD_STUDENTS_EMPTY_ERROR,
    ADD_STUDENTS_INVALID_FILE,
    ADD_STUDENTS_CSV_ROW_MISSING_NAME,
    ADD_STUDENTS_CSV_ROW_EXTRA_COLUMNS,
    ADD_STUDENTS_UPLOAD,
    ADD_STUDENTS_DOWNLOAD,
}                           from "config/config";

// Parse the names-only CSV template ("First name,The first letter of last name") on the
// client (review #340 item 2). Only the two name columns are read; a legacy 4-column file
// (name, last name, DOB, parent email) imports its names with a per-row warning instead
// of silently dropping data. Naive comma split on purpose — the template has no quoted
// fields; a name containing a comma surfaces as an extra-columns warning for the teacher
// to fix in the textarea.
function parseNamesCsv(text: string): { names: string[]; problems: string[] } {
    const names: string[] = [];
    const problems: string[] = [];
    const lines = text.split(/\r\n|\r|\n/);
    lines.forEach((line, idx) => {
        if (!line.trim()) return;
        const cells = line.split(',').map((c) => c.replace(/^"|"$/g, '').trim());
        // Header row of the template (and of the legacy template).
        if (idx === 0 && /first[\s_]*name/i.test(cells[0])) return;
        const rowNo = idx + 1;
        if (!cells[0]) {
            problems.push(`Row ${rowNo}: ${ADD_STUDENTS_CSV_ROW_MISSING_NAME}`);
            return;
        }
        if (cells.slice(2).some(Boolean)) {
            problems.push(`Row ${rowNo}: ${ADD_STUDENTS_CSV_ROW_EXTRA_COLUMNS}`);
        }
        names.push([cells[0], cells[1]].filter(Boolean).join(' '));
    });
    return { names, problems };
}

// Body of the "Add students" step (names textarea + CSV upload). Pure client-side: the
// CSV is parsed here and lands in the textarea for the teacher to review/edit; nothing
// is created until Review is confirmed (ClassFlow). Draft state is local so typing
// rerenders only this form (review #340 item 5); onDraft mirrors it to the host's ref.
export default function AddStudentsFields({
    initialNames,
    onDraft,
    onSubmit,
}: {
    initialNames: string;
    onDraft: (names: string) => void;
    onSubmit: (names: string[]) => void;
}) {
    const [names, setNames] = React.useState(initialNames);
    const [error, setError] = React.useState('');          // empty-field validation
    const [csvProblems, setCsvProblems] = React.useState<string[]>([]);

    const setNamesEverywhere = (value: string) => {
        setNames(value);
        onDraft(value);
    };

    const submitNames = () => {
        const list = names.split('\n').map((n) => n.trim()).filter(Boolean);
        if (list.length === 0) {
            setError(ADD_STUDENTS_EMPTY_ERROR);
            return;
        }
        onSubmit(list);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.item(0);
        e.target.value = '';  // allow re-selecting the same file
        if (!file) {
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const { names: parsed, problems } = parseNamesCsv(String(reader.result ?? ''));
            if (parsed.length === 0) {
                setCsvProblems([ADD_STUDENTS_INVALID_FILE, ...problems]);
                return;
            }
            setCsvProblems(problems);
            // Append into the textarea so the teacher sees exactly what will be added.
            setNamesEverywhere([names.trim(), parsed.join('\n')].filter(Boolean).join('\n'));
            if (error) setError('');
        };
        reader.onerror = () => setCsvProblems([ADD_STUDENTS_INVALID_FILE]);
        reader.readAsText(file);
    };

    return (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
                fullWidth
                multiline
                minRows={5}
                maxRows={10}
                value={names}
                placeholder={ADD_STUDENTS_PLACEHOLDER}
                error={!!error}
                helperText={error}
                onChange={(e) => {
                    setNamesEverywhere(e.target.value);
                    if (error) setError('');
                }}
            />
            <Button
                fullWidth
                variant="contained"
                onClick={submitNames}
                sx={{ boxShadow: 'none' }}
            >
                {ADD_STUDENTS_BUTTON}
            </Button>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: 2 }}>
                <input id="fileInput" hidden type="file" accept=".csv" onChange={handleFileChange} />
                <Link
                    component="button"
                    type="button"
                    underline="none"
                    onClick={() => document.getElementById('fileInput')?.click()}
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '1rem', fontWeight: 700 }}
                >
                    <FileUploadOutlined fontSize="small" />
                    {ADD_STUDENTS_UPLOAD}
                </Link>
                <Link href="/funexpected-template.csv" download underline="none">
                    {ADD_STUDENTS_DOWNLOAD}
                </Link>
            </Box>
            {csvProblems.length > 0 &&
                <Box sx={{ alignSelf: 'flex-start' }}>
                    {csvProblems.map((problem, idx) => (
                        <Typography key={idx} color="error" variant="body2">
                            {problem}
                        </Typography>
                    ))}
                </Box>
            }
        </Box>
    );
}
